/*
Risk Impacts Service

This service handles all API calls for risk impacts.
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

const apiService = apiClient;

export interface RiskImpact {
  id: number;
  entity_id: number;
  impact_code?: string;
  impact_name: string;
  impact_description?: string;
  is_active: number;
  is_approved: number;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

export interface ImpactQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class RiskImpactsService {
  async getAll(params?: ImpactQueryParams): Promise<RiskImpact[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/rsk/impacts${queryString ? `?${queryString}` : ''}`;

      const response: AxiosResponse<ApiResponse<RiskImpact[]>> = await apiService.get(url);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk impacts');
      }
    } catch (error: any) {
      console.error('Error fetching risk impacts:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /** Approved + active impacts only, for link/select dropdowns. */
  async getDropdown(): Promise<{ id: number; name: string }[]> {
    const response = await apiService.get('/api/v1/rsk/impacts/dropdown');
    return response.data?.items ?? [];
  }

  async getById(impactId: number): Promise<RiskImpact> {
    try {
      const response: AxiosResponse<ApiResponse<RiskImpact>> =
        await apiService.get(`/api/v1/rsk/impacts/${impactId}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk impact');
      }
    } catch (error: any) {
      console.error(`Error fetching risk impact ${impactId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async create(impactData: Partial<RiskImpact>): Promise<RiskImpact> {
    try {
      const response: AxiosResponse<ApiResponse<RiskImpact>> =
        await apiService.post('/api/v1/rsk/impacts', impactData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create risk impact');
      }
    } catch (error: any) {
      console.error('Error creating risk impact:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async update(impactId: number, updateData: Partial<RiskImpact>): Promise<RiskImpact> {
    try {
      const response: AxiosResponse<ApiResponse<RiskImpact>> =
        await apiService.put(`/api/v1/rsk/impacts/${impactId}`, updateData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update risk impact');
      }
    } catch (error: any) {
      console.error(`Error updating risk impact ${impactId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async delete(impactId: number): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> =
        await apiService.delete(`/api/v1/rsk/impacts/${impactId}`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete risk impact');
      }
    } catch (error: any) {
      console.error(`Error deleting risk impact ${impactId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async toggleStatus(impactId: number): Promise<RiskImpact> {
    try {
      const response: AxiosResponse<ApiResponse<RiskImpact>> =
        await apiService.patch(`/api/v1/rsk/impacts/${impactId}/toggle`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to toggle impact status');
      }
    } catch (error: any) {
      console.error(`Error toggling risk impact ${impactId} status:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async submit(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/impacts/${id}/submit`);
    if (!res.data.success) throw new Error(res.data.error || 'Submit failed');
  }
  async approve(id: number, note?: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/impacts/${id}/approve`, { note });
    if (!res.data.success) throw new Error(res.data.error || 'Approve failed');
  }
  async reject(id: number, reason: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/impacts/${id}/reject`, { reason });
    if (!res.data.success) throw new Error(res.data.error || 'Reject failed');
  }
  async reopen(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/impacts/${id}/reopen`);
    if (!res.data.success) throw new Error(res.data.error || 'Reopen failed');
  }

  async getHistory(id: number): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> {
    const response = await apiService.get(`/api/v1/rsk/impacts/${id}/history`);
    const rows = response.data?.data ?? response.data?.items ?? [];
    return (rows as any[]).map((r: any) => ({
      audit_id: r.id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  }
}

const riskImpactsService = new RiskImpactsService();
export default riskImpactsService;
